/**
 * Which of the hunt's own overlays may be on screen, and when.
 *
 * ## Why this is a function and not two conditions in the App
 *
 * There are exactly two, they both interact with PvP, and both interactions
 * were found by looking at a screenshot rather than by reading the code:
 *
 * 1. **The death screen must come back when a dead run is re-adopted, and only
 *    then.** Coming home from a match, the hunt is re-authorised through
 *    `hunt:session`, which publishes `hunt:restarted`, which clears the overlay
 *    - and adopting a snapshot produces no `ship-death` event to raise it
 *    again. The player landed on "ABATIDA", 0/100, with nothing to press.
 *
 *    That fix used to be stated as "shown the moment it arrives, PvP screen or
 *    not", on the assumption that a PvP screen and a death could only coexist on
 *    the way home. Gate V24D.1C photographed the counter-example: F5 during a
 *    live duel asks for the hunt's bootstrap and for the match at the same time,
 *    the bootstrap answers with a hunt that died earlier, and NAVE DESTRUÍDA
 *    landed over a running arena - covering both health bars, the clock and the
 *    two ships. So the death now WAITS for the arena to go, which is the way
 *    home it was always about.
 * 2. **The offline report must WAIT while a PvP screen is up.** A reload during
 *    a match asks for the hunt's bootstrap and for the match this account is
 *    still in, at the same time. The offline dialog is a backdrop, so it landed
 *    over the reconnection and asked the player to read a report before they
 *    could get back into a fight the room was holding a chair in for twelve
 *    seconds.
 *
 * Two rules with two reasons, living in JSX where nothing could test them. They
 * live here now, and the App renders what this returns.
 *
 * ## Deferred is not dismissed
 *
 * Nothing here consumes, clears, recalculates or grants anything. The offline
 * report is the same object the server sent; when the PvP screen goes, it is
 * rendered unchanged. The only thing that ever clears it is a person pressing
 * its own button.
 */

export interface HuntOverlayView {
  /** The canonical death screen, with its canonical restart. */
  readonly deathScreen: boolean;
  /** The offline farm report. Held back rather than dropped. */
  readonly offlineReport: boolean;
}

/**
 * Whether a PvP arena owns the screen right now, in either of its two ways.
 *
 * Open OR being restored. The second half is not a detail: `panelOpen` is
 * `state.kind !== 'closed'`, and a reloaded tab is `closed` until
 * `GET /api/pvp/active` answers - so a rule written on `panelOpen` alone still
 * lets the hunt's overlay flash over an arena that is one round trip from
 * existing.
 */
function pvpOwnsTheScreen(input: {
  readonly pvpPanelOpen: boolean;
  readonly pvpRestoring: boolean;
}): boolean {
  return input.pvpPanelOpen || input.pvpRestoring;
}

/**
 * Está OUTRO ecrã a ser dono do ecrã — um duelo, ou uma expedição.
 *
 * A expedição entrou por aqui pela mesma razão que o duelo entrou: uma captura.
 * O relatório de farm offline é um `backdrop`, e ele aterrou por cima de um
 * quadrante do mundo aberto — a tapar a nave, o HUD e o painel da fronteira, e
 * a intercetar o clique em PREPARAR EXTRAÇÃO. A caça está parada e escondida
 * durante uma expedição, exatamente como durante um duelo, portanto os dois
 * overlays dela esperam pelo mesmo motivo.
 *
 * **Esperar não é descartar.** Nada aqui consome, limpa ou recalcula: o
 * relatório é o mesmo objeto que o servidor mandou, e ele aparece intacto no
 * quadro seguinte ao fim da expedição.
 */
function anotherScreenOwnsIt(input: {
  readonly pvpPanelOpen: boolean;
  readonly pvpRestoring: boolean;
  readonly openWorldActive: boolean;
}): boolean {
  return pvpOwnsTheScreen(input) || input.openWorldActive;
}

export function huntOverlayView(input: {
  /** Whether the hunt has a death the player has not restarted from. */
  readonly hasDeath: boolean;
  /** Whether the server has handed over an offline window nobody dismissed. */
  readonly hasOfflineReward: boolean;
  /** Whether ANY PvP screen is up - a question, a lobby, a reconnection, an arena. */
  readonly pvpPanelOpen: boolean;
  /**
   * Whether this tab is still asking the server whether it is in a match.
   *
   * True from the moment the discovery is sent until it answers, either way. It
   * is deliberately NOT "the panel is open": the panel opens only when there is
   * something to open it for, which is a round trip later.
   */
  readonly pvpRestoring: boolean;
  /**
   * Whether an open-world expedition owns the screen.
   *
   * True from the moment a quadrant is live until the receipt is dismissed. The
   * hunt is paused and hidden throughout, so neither overlay is being hidden
   * from a player who could act on it - it is being held until they are
   * somewhere it makes sense to read it.
   */
  readonly openWorldActive: boolean;
}): HuntOverlayView {
  const pvpOwns = anotherScreenOwnsIt(input);
  return {
    /*
     * Deferred exactly as long as a PvP arena owns the screen (gate V24D.2).
     *
     * The overlay a player has to act on must never be lost - and it is not:
     * `hasDeath` is state, not an event, so the frame after the arena goes this
     * renders the same death with the same restart. What changed is that it no
     * longer renders ON TOP of a match, which is what a capture caught it doing
     * after an F5.
     *
     * The hunt is paused during a match and cannot die there, so this is never
     * hiding something that just happened: it is holding something that
     * happened BEFORE, until the player is somewhere it makes sense to read it.
     */
    deathScreen: input.hasDeath && !pvpOwns,
    /*
     * Deferred while a PvP screen is up, AND while a death is unresolved.
     *
     * Two conditions because there are two things it may not cover, and both
     * were found in a capture rather than in the code:
     *
     * - `pvpPanelOpen`, rather than "the arena is up": the first screen it
     *   covered was the RECONNECTION, which is not a dedicated mode yet.
     * - `hasDeath`, because the second thing it covered was the death screen
     *   itself - a backdrop over the one overlay the player has to act on,
     *   which is the same "no way out" this pair of fixes exists to remove.
     *
     * The order that results is the approved one: an active match, then the
     * death and its restart, then the report. Nothing is consumed by waiting;
     * the restart clears the death and this renders the server's own object
     * unchanged on the next frame.
     */
    offlineReport: input.hasOfflineReward && !pvpOwns && !input.hasDeath,
  };
}
